import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, MessagesSquare, HelpCircle, CalendarDays, Users, TriangleAlert, Plus, Sparkles, Flag, Send, Loader2, History, MessageCircle } from "lucide-react";
import { T } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  BACKEND_BASE, communityPhotos, uploadCommunityPhoto,
  chatThreads, chatThread, createChatThread, createChatReply, flagChatThread,
} from "@/lib/api";

/** In-app, geo-wired SocialPulse network surfaces (PhotoPulse + ChatsPulse). */

// ---------------------------------------------------------------- Build CTA --
export function BuildPageCTA({ placeName, onContribute }) {
  return (
    <div className="rounded-2xl border-2 border-amber-300/70 bg-amber-50 p-5 sm:p-6" data-testid="build-page-cta">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-amber-200/70 px-3 py-0.5 text-[11px] font-bold uppercase tracking-widest text-amber-900">
            <Sparkles className="h-3.5 w-3.5" /> <T>Help build this page</T>
          </div>
          <h3 className="font-display text-xl font-black text-navy">{placeName}<T>'s page is still growing</T></h3>
          <p className="mt-1 text-sm text-amber-900/80">
            <T>This page isn't fully published yet. Add local knowledge — stories, history, photos, events, organizations or corrections — and help your community put it on the map.</T>
          </p>
        </div>
        <button onClick={onContribute} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-bold text-white transition-colors hover:bg-navy-800" data-testid="build-page-contribute-btn">
          <Plus className="h-4 w-4" /> <T>Contribute to this community</T>
        </button>
      </div>
    </div>
  );
}

function PanelHead({ icon: Icon, title, badge, note, action }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-3 border-b border-border pb-3">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy text-white"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-2xl font-black tracking-tight text-navy sm:text-3xl"><T>{title}</T></h2>
            <span className="rounded bg-oldglory-red/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-oldglory-red">{badge}</span>
          </div>
          <p className="text-xs text-muted-foreground">{note}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

// ------------------------------------------------------------- PhotoPulse ----
export function PhotoPulsePanel({ data }) {
  const geoId = data.geo_id;
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [credit, setCredit] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { communityPhotos(geoId).then((d) => setPhotos(d.photos || [])).catch(() => {}); }, [geoId]);
  useEffect(() => { load(); }, [load]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB."); return; }
    setFile(f); setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) { toast.error("Please choose a photo."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("geo_id", geoId);
      fd.append("caption", caption); fd.append("credit", credit);
      const r = await uploadCommunityPhoto(fd);
      toast.success(r.message || "Thank you! Your photo is awaiting review.");
      setOpen(false); setFile(null); setPreview(null); setCaption(""); setCredit("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally { setBusy(false); }
  };

  const addBtn = (
    <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-navy-800" data-testid="photopulse-add-btn">
      <Camera className="h-4 w-4" /> <T>Add a photo</T>
    </button>
  );

  return (
    <section data-testid="photopulse-panel">
      <PanelHead icon={Camera} title="Community Photos" badge="PhotoPulse" note={`Local imagery of ${data.official_name}`} action={addBtn} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {data.image_url && (
          <div className="col-span-2 overflow-hidden rounded-xl border border-border"><img src={data.image_url} alt={data.official_name} className="h-full w-full object-cover" /></div>
        )}
        {photos.map((p) => (
          <figure key={p.id} className="overflow-hidden rounded-xl border border-border bg-white" data-testid={`community-photo-${p.id}`}>
            <img src={`${BACKEND_BASE}${p.url}`} alt={p.caption || data.official_name} className="aspect-square w-full object-cover" loading="lazy" />
            {(p.caption || p.credit) && (
              <figcaption className="p-2 text-[10px] leading-tight text-muted-foreground">
                {p.caption && <span className="block text-ink">{p.caption}</span>}
                {p.credit && <span>{`Photo: ${p.credit}`}</span>}
              </figcaption>
            )}
          </figure>
        ))}
        {Array.from({ length: Math.max(0, (data.image_url ? 2 : 4) - photos.length) }).map((_, i) => (
          <button key={i} onClick={() => setOpen(true)} className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border bg-parchment/40 text-muted-foreground transition-colors hover:border-navy/40 hover:text-navy" data-testid={`photopulse-add-tile-${i}`}>
            <Camera className="h-5 w-5 opacity-60" /><span className="text-[11px] font-semibold"><T>Add a photo</T></span>
          </button>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground"><T>Photos are reviewed before they appear. Part of the SocialPulse network.</T></p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="photo-upload-dialog">
          <DialogHeader><DialogTitle>Add a photo of {data.official_name}</DialogTitle>
            <DialogDescription className="sr-only">Upload a local photo; it is reviewed before appearing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex h-40 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-border bg-parchment/40">
              {preview ? <img src={preview} alt="preview" className="h-full w-full object-cover" /> : <span className="flex flex-col items-center gap-1 text-sm text-muted-foreground"><Camera className="h-6 w-6" /> Choose an image (JPG/PNG/WEBP, &lt;8MB)</span>}
              <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="photo-file-input" />
            </label>
            <div><Label htmlFor="cap" className="text-xs">Caption</Label><Input id="cap" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="What's in this photo?" data-testid="photo-caption-input" /></div>
            <div><Label htmlFor="cred" className="text-xs">Your name / credit</Label><Input id="cred" value={credit} onChange={(e) => setCredit(e.target.value)} placeholder="Optional" data-testid="photo-credit-input" /></div>
          </div>
          <DialogFooter>
            <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-bold text-white disabled:opacity-60" data-testid="photo-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} <T>Submit for review</T>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ------------------------------------------------------------- ChatsPulse ----
const TOPICS = [
  { key: "questions", icon: HelpCircle, label: "Local Questions" },
  { key: "events", icon: CalendarDays, label: "Events & Happenings" },
  { key: "neighborhood", icon: Users, label: "Neighborhood Talk" },
  { key: "alerts", icon: TriangleAlert, label: "Roads, Weather & Alerts" },
  { key: "history", icon: History, label: "Local History" },
  { key: "general", icon: MessageCircle, label: "General" },
];
const TOPIC_LABEL = Object.fromEntries(TOPICS.map((t) => [t.key, t.label]));

export function ChatsPulsePanel({ data }) {
  const geoId = data.geo_id;
  const placeName = data.official_name;
  const [counts, setCounts] = useState({});
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [detail, setDetail] = useState(null); // {thread, replies}
  const [form, setForm] = useState({ topic: "questions", title: "", body: "", author_name: "" });
  const [reply, setReply] = useState({ body: "", author_name: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    chatThreads(geoId, filter).then((d) => { setThreads(d.threads || []); setCounts(d.counts || {}); }).catch(() => {});
  }, [geoId, filter]);
  useEffect(() => { load(); }, [load]);

  const openStart = (topic) => { setForm({ topic: topic || "questions", title: "", body: "", author_name: "" }); setStartOpen(true); };

  const submitThread = async () => {
    if (form.title.trim().length < 4 || form.body.trim().length < 4) { toast.error("Add a short title and message."); return; }
    setBusy(true);
    try {
      const r = await createChatThread({ geo_id: geoId, ...form });
      toast.success(r.message || "Posted!");
      setStartOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post."); } finally { setBusy(false); }
  };

  const openThread = async (tid) => {
    try { const d = await chatThread(tid); setDetail(d); setReply({ body: "", author_name: "" }); }
    catch { toast.error("Could not open discussion."); }
  };

  const submitReply = async () => {
    if (reply.body.trim().length < 2) { toast.error("Write a reply."); return; }
    setBusy(true);
    try {
      const r = await createChatReply(detail.thread.id, reply);
      toast.success(r.message || "Reply posted!");
      const d = await chatThread(detail.thread.id); setDetail(d); setReply({ body: "", author_name: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reply."); } finally { setBusy(false); }
  };

  const flag = async (tid) => { try { await flagChatThread(tid); toast.success("Reported — thank you. Our moderators will review it."); } catch { toast.error("Could not report."); } };

  const startBtn = (
    <button onClick={() => openStart()} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-navy-800" data-testid="chats-start-btn">
      <Plus className="h-4 w-4" /> <T>Start a discussion</T>
    </button>
  );

  return (
    <section data-testid="chatspulse-panel">
      <PanelHead icon={MessagesSquare} title="Community Conversations" badge="ChatsPulse" note={`Ask, share and discuss what's happening in ${placeName}`} action={startBtn} />

      <div className="mb-4 flex flex-wrap gap-2">
        <button onClick={() => setFilter(null)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${!filter ? "bg-navy text-white" : "bg-navy/5 text-navy hover:bg-navy/10"}`} data-testid="chats-filter-all"><T>All</T></button>
        {TOPICS.map((t) => (
          <button key={t.key} onClick={() => setFilter(t.key)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === t.key ? "bg-navy text-white" : "bg-navy/5 text-navy hover:bg-navy/10"}`} data-testid={`chats-topic-${t.key}`}>
            <t.icon className="h-3.5 w-3.5" /> <T>{t.label}</T> <span className="opacity-70">{counts[t.key] || 0}</span>
          </button>
        ))}
      </div>

      {threads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-parchment/40 p-6 text-center">
          <p className="text-sm text-muted-foreground"><T>No conversations yet. Be the first to start one.</T></p>
          <button onClick={() => openStart(filter)} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-bold text-white" data-testid="chats-empty-start"><Plus className="h-4 w-4" /> <T>Start a discussion</T></button>
        </div>
      ) : (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-white">
          {threads.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 px-4 py-3 transition-colors hover:bg-parchment/50" data-testid={`chats-thread-${t.id}`}>
              <button onClick={() => openThread(t.id)} className="min-w-0 flex-1 text-left">
                <span className="mb-0.5 inline-block rounded bg-navy/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-navy/70">{TOPIC_LABEL[t.topic] || t.topic}</span>
                <span className="block truncate font-display text-base font-bold text-navy">{t.title}</span>
                <span className="text-xs text-muted-foreground">{t.author_name} · {t.reply_count} {t.reply_count === 1 ? "reply" : "replies"}</span>
              </button>
              <button onClick={() => flag(t.id)} title="Report" className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-oldglory-red/10 hover:text-oldglory-red" data-testid={`chats-flag-${t.id}`}><Flag className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground"><T>Discussions are moderated and can be reported. Part of the SocialPulse network.</T></p>

      {/* Start-discussion dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="max-w-lg" data-testid="chats-start-dialog">
          <DialogHeader><DialogTitle>Start a discussion about {placeName}</DialogTitle>
            <DialogDescription className="sr-only">Pick a topic and share a question or update with your community.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Topic</Label>
              <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" data-testid="chats-topic-select">
                {TOPICS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div><Label htmlFor="tt" className="text-xs">Title</Label><Input id="tt" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Best farmers market?" data-testid="chats-title-input" /></div>
            <div><Label htmlFor="tb" className="text-xs">Message</Label><Textarea id="tb" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Share your question or update…" data-testid="chats-body-input" /></div>
            <div><Label htmlFor="tn" className="text-xs">Your name (optional)</Label><Input id="tn" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="Neighbor" data-testid="chats-name-input" /></div>
          </div>
          <DialogFooter>
            <button onClick={submitThread} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-bold text-white disabled:opacity-60" data-testid="chats-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} <T>Post</T>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thread detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="chats-thread-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.thread.title}</DialogTitle>
                <DialogDescription className="sr-only">Community discussion with replies.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-lg bg-parchment/50 p-3">
                  <p className="whitespace-pre-wrap text-sm text-ink">{detail.thread.body}</p>
                  <p className="mt-2 text-xs text-muted-foreground">— {detail.thread.author_name}</p>
                </div>
                <div className="space-y-2">
                  {detail.replies.length === 0 ? <p className="text-sm text-muted-foreground"><T>No replies yet.</T></p>
                    : detail.replies.map((r) => (
                      <div key={r.id} className="rounded-lg border border-border p-3" data-testid={`chats-reply-${r.id}`}>
                        <p className="whitespace-pre-wrap text-sm text-ink">{r.body}</p>
                        <p className="mt-1 text-xs text-muted-foreground">— {r.author_name}</p>
                      </div>
                    ))}
                </div>
                <div className="space-y-2 border-t border-border pt-3">
                  <Textarea rows={3} value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })} placeholder="Add a reply…" data-testid="chats-reply-input" />
                  <div className="flex items-center gap-2">
                    <Input value={reply.author_name} onChange={(e) => setReply({ ...reply, author_name: e.target.value })} placeholder="Your name (optional)" className="flex-1" data-testid="chats-reply-name" />
                    <button onClick={submitReply} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-bold text-white disabled:opacity-60" data-testid="chats-reply-submit">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} <T>Reply</T>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
